import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ColorPicker, Dropdown, Modal, Skeleton, Tooltip } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import {
    deleteStoryMapAttachment,
    formatReleaseDateRange,
    getStoryMapAttachmentModule,
    getStoryMapAttachmentUploadUrls,
    isAllowedStoryMapAttachment,
    isMongoObjectId,
    STORY_MAP_ATTACHMENT_ACCEPT,
} from '../../../storyMap.service'
import { uploadAttachmentToAWS } from '../../../../taskRefactored/tasks.service'
import { showActionNotification } from '../../../../../components/notification'
import { loginState } from '../../../../login/login.slice'

import DateRangePicker from '../../DateRangePicker'
import StoryMapDescriptionEditor, {
    descriptionsAreEqual,
    normalizeHtml,
} from './StoryMapDescriptionEditor'
import {
    DEFAULT_STORY_STATUS,
    getDefaultCardAccentColor,
    isDefaultCardColor,
    isUntitledName,
    normalizeHexColor,
    normalizeStoryStatus,
    parseJourneyLabel,
    parseReleaseDates,
    parseStepLabel,
    resolveEditTitleName,
} from '../utils'
import StoryMapCardDetailSkeleton from './StoryMapCardDetailSkeleton'
import StoryMapCommentsFooter from './StoryMapCommentsFooter'
import StoryMapDeleteConfirm from './StoryMapDeleteConfirm'
import { prependStoryMapDescriptionActivity } from './storyMapActivityLocal'
import StoryMapStatusDropdown from './StoryMapStatusDropdown'
import styles from './StoryMapCardDetailModal.module.css'

const COMMENT_PAGE_SIZE = 10

const DRAWER_TITLE_BY_TYPE = {
    step: 'Step details',
    story: 'Story details',
    journey: 'Journey details',
    release: 'Release details',
}

const getAttachmentFileIcon = (name = '') => {
    const lower = String(name).toLowerCase()
    if (/\.(png|jpe?g|gif|svg|webp)$/.test(lower)) {
        return 'mdi:file-image-outline'
    }
    if (lower.endsWith('.pdf')) {
        return 'mdi:file-pdf-box'
    }
    if (lower.endsWith('.csv')) {
        return 'mdi:file-delimited-outline'
    }
    return 'mdi:file-outline'
}

const getAttachmentKind = (name = '') => {
    const lower = String(name).toLowerCase()
    if (/\.(png|jpe?g|gif|svg|webp)$/.test(lower)) return 'image'
    if (lower.endsWith('.pdf')) return 'pdf'
    if (lower.endsWith('.csv')) return 'csv'
    return 'file'
}

const isAttachmentImage = (name = '') => getAttachmentKind(name) === 'image'

const triggerBrowserDownload = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.setAttribute('download', fileName || 'download')
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000)
}

const createDownloadBlob = (buffer) => new Blob([buffer], { type: 'application/octet-stream' })

const downloadImageViaCanvas = (url, fileName) =>
    new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            canvas.getContext('2d').drawImage(img, 0, 0)

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Could not create image blob'))
                        return
                    }

                    triggerBrowserDownload(blob, fileName)
                    resolve()
                },
                'image/png'
            )
        }
        img.onerror = () => reject(new Error('Image load failed'))
        img.src = url
    })

const downloadAttachment = async (item, event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (!item?.url) return

    const fileName = item.name || 'download'

    try {
        const response = await fetch(item.url, { mode: 'cors', cache: 'no-store' })
        if (!response.ok) throw new Error('Download failed')

        const buffer = await response.arrayBuffer()
        triggerBrowserDownload(createDownloadBlob(buffer), fileName)
    } catch (error) {
        if (isAttachmentImage(fileName)) {
            try {
                await downloadImageViaCanvas(item.url, fileName)
                return
            } catch (canvasError) {
                // Fall through to error notification.
            }
        }

        showActionNotification({
            type: 'error',
            message: 'Failed to download attachment.',
        })
    }
}

const AttachmentImagePreviewModal = ({ item, open, onClose }) => (
    <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        width="min(92vw, 960px)"
        className={styles.imagePreviewModal}
        destroyOnClose
        title={item?.name || 'Image preview'}
    >
        {item?.url ? (
            <img
                src={item.url}
                alt={item.name || 'Attachment preview'}
                className={styles.imagePreviewImg}
            />
        ) : null}
    </Modal>
)

const AttachmentImageCard = ({ item, onDelete, onPreview }) => {
    const [imageError, setImageError] = useState(false)
    const showThumb = Boolean(item.url) && !imageError

    const handlePreview = (event) => {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        if (item.url) onPreview?.(item)
    }

    const handleDownload = (event) => {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        downloadAttachment(item, event)
    }

    const stopCardActionEvent = (event) => {
        event.preventDefault()
        event.stopPropagation()
    }

    return (
        <li className={styles.attachmentImageCard}>
            <div
                className={`${styles.attachmentImageCardPreview} ${
                    item.url ? styles.attachmentImageCardPreviewClickable : ''
                }`}
                onClick={
                    item.url
                        ? (event) => {
                              if (
                                  event.target.closest(
                                      `.${styles.attachmentImageCardActions}`
                                  )
                              ) {
                                  return
                              }
                              handlePreview(event)
                          }
                        : undefined
                }
                onKeyDown={
                    item.url
                        ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  handlePreview(event)
                              }
                          }
                        : undefined
                }
                role={item.url ? 'button' : undefined}
                tabIndex={item.url ? 0 : undefined}
                aria-label={item.url ? `Preview ${item.name}` : undefined}
            >
                {showThumb ? (
                    <img
                        src={item.url}
                        alt={item.name}
                        className={styles.attachmentImageCardThumb}
                        loading="lazy"
                        onError={() => setImageError(true)}
                        draggable={false}
                    />
                ) : (
                    <span className={styles.attachmentImageCardFallback}>
                        <Icon icon="mdi:image-outline" width={22} />
                    </span>
                )}
                <div
                    className={styles.attachmentImageCardActions}
                    onClick={stopCardActionEvent}
                    onMouseDown={stopCardActionEvent}
                >
                    {item.url ? (
                        <button
                            type="button"
                            className={styles.attachmentActionBtn}
                            aria-label={`Preview ${item.name}`}
                            onClick={handlePreview}
                            onMouseDown={stopCardActionEvent}
                        >
                            <Icon icon="mdi:arrow-expand" width={14} />
                        </button>
                    ) : null}
                    {item.url ? (
                        <button
                            type="button"
                            className={styles.attachmentActionBtn}
                            aria-label={`Download ${item.name}`}
                            onClick={handleDownload}
                            onMouseDown={stopCardActionEvent}
                        >
                            <Icon icon="mdi:download" width={14} />
                        </button>
                    ) : null}
                    {onDelete ? (
                    <button
                        type="button"
                        className={`${styles.attachmentActionBtn} ${styles.attachmentActionBtnDanger}`}
                        aria-label={`Delete ${item.name}`}
                        onClick={(event) => {
                            stopCardActionEvent(event)
                            onDelete(item.id)
                        }}
                        onMouseDown={stopCardActionEvent}
                    >
                        <Icon icon="mdi:delete-outline" width={14} />
                    </button>
                    ) : null}
                </div>
            </div>
            <span className={styles.attachmentImageCardName} title={item.name}>
                {item.name}
            </span>
        </li>
    )
}

const AttachmentFileRow = ({ item, onDelete }) => {
    const kind = getAttachmentKind(item.name)

    const handleDownload = (event) => {
        event.preventDefault()
        event.stopPropagation()
        downloadAttachment(item, event)
    }

    return (
        <li className={styles.attachmentFileRow}>
            <span
                className={`${styles.attachmentFileRowIcon} ${
                    styles[`attachmentFileRowIcon_${kind}`] || ''
                }`}
            >
                <Icon icon={getAttachmentFileIcon(item.name)} width={18} />
            </span>
            <div className={styles.attachmentFileRowBody}>
                <span className={styles.attachmentFileRowName} title={item.name}>
                    {item.name}
                </span>
                <span
                    className={`${styles.attachmentFileRowType} ${
                        styles[`attachmentCardType_${kind}`] || ''
                    }`}
                >
                    {kind}
                </span>
            </div>
            <div className={styles.attachmentFileRowActions}>
                {item.url ? (
                    <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentRowActionBtn}
                        aria-label={`Open ${item.name}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Icon icon="mdi:open-in-new" width={16} />
                    </a>
                ) : null}
                {item.url ? (
                    <button
                        type="button"
                        className={styles.attachmentRowActionBtn}
                        aria-label={`Download ${item.name}`}
                        onClick={handleDownload}
                    >
                        <Icon icon="mdi:download" width={16} />
                    </button>
                ) : null}
                {onDelete ? (
                <button
                    type="button"
                    className={`${styles.attachmentRowActionBtn} ${styles.attachmentRowActionBtnDanger}`}
                    aria-label={`Delete ${item.name}`}
                    onClick={() => onDelete(item.id)}
                >
                    <Icon icon="mdi:delete-outline" width={16} />
                </button>
                ) : null}
            </div>
        </li>
    )
}

const COLOR_OPTIONS = [
    { id: 'default', color: '#ffffff' },
    { id: 'orange', color: '#f97316' },
    { id: 'blue', color: '#3b82f6' },
    { id: 'green', color: '#22c55e' },
    { id: 'yellow', color: '#eab308' },
    { id: 'red', color: '#ef4444' },
    { id: 'purple', color: '#a855f7' },
    { id: 'pink', color: '#ec4899' },
    { id: 'grey', color: '#6b7280' },
    { id: 'black', color: '#111827' },
]

const isPresetCardColor = (color) => {
    if (isDefaultCardColor(color)) return true
    const normalized = normalizeHexColor(color).toLowerCase()
    return COLOR_OPTIONS.some(
        (option) =>
            !isDefaultCardColor(option.color) &&
            normalizeHexColor(option.color).toLowerCase() === normalized
    )
}

const resolvePickerHex = (color, css) => {
    if (color?.toHexString) {
        return normalizeHexColor(color.toHexString())
    }
    if (color?.toHex) {
        return normalizeHexColor(color.toHex())
    }
    if (typeof color === 'string') {
        return normalizeHexColor(color)
    }
    if (css && typeof css === 'string' && css.trim().startsWith('#')) {
        return normalizeHexColor(css)
    }
    return ''
}

const ColorDropdown = ({ value, onChange, cardType, popupContainerRef, readOnly = false }) => {
    const menuRef = useRef(null)
    const draftColorRef = useRef(null)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [draftColor, setDraftColor] = useState(null)
    const defaultAccent = getDefaultCardAccentColor(cardType)
    const isDefault = isDefaultCardColor(value)
    const isCustomSelected = !isDefault && !isPresetCardColor(value)
    const customColorValue = normalizeHexColor(value) || defaultAccent
    const pickerColorValue = draftColor || customColorValue

    const handlePickerOpenChange = (open) => {
        if (open) {
            draftColorRef.current = customColorValue
            setDraftColor(customColorValue)
        } else {
            draftColorRef.current = null
            setDraftColor(null)
        }
        setPickerOpen(open)
    }

    const handleDraftColorChange = (color, css) => {
        const nextHex = resolvePickerHex(color, css)
        if (!nextHex) return
        draftColorRef.current = nextHex
        setDraftColor(nextHex)
    }

    const handleCustomColorOkay = (event) => {
        event.preventDefault()
        event.stopPropagation()
        const nextHex = normalizeHexColor(draftColorRef.current || pickerColorValue)
        if (!nextHex) return
        onChange(nextHex)
        draftColorRef.current = null
        setDraftColor(null)
        setPickerOpen(false)
    }

    const isOptionSelected = (optionColor) => {
        if (isDefaultCardColor(optionColor)) return isDefault
        return (
            normalizeHexColor(value).toLowerCase() ===
            normalizeHexColor(optionColor).toLowerCase()
        )
    }

    const menu = (
        <div className={styles.colorPicker} ref={menuRef}>
            {COLOR_OPTIONS.map((option) => {
                const isWhiteOption = isDefaultCardColor(option.color)
                const selected = isOptionSelected(option.color)

                return (
                    <button
                        key={option.id}
                        type="button"
                        className={`${styles.colorOption} ${
                            selected ? styles.colorOptionSelected : ''
                        } ${isWhiteOption ? styles.colorOptionDefault : ''}`}
                        style={
                            isWhiteOption
                                ? { borderTopColor: defaultAccent }
                                : { background: option.color }
                        }
                        onClick={() => onChange(option.color)}
                        aria-label={`Select ${option.id} color`}
                    >
                        {selected ? (
                            <Icon
                                icon="mdi:check"
                                width={14}
                                color={isWhiteOption ? defaultAccent : '#fff'}
                            />
                        ) : null}
                    </button>
                )
            })}
            <ColorPicker
                value={pickerColorValue}
                open={pickerOpen}
                onOpenChange={handlePickerOpenChange}
                disabledAlpha
                format="hex"
                onChange={handleDraftColorChange}
                onChangeComplete={handleDraftColorChange}
                destroyTooltipOnHide
                getPopupContainer={() =>
                    menuRef.current ||
                    popupContainerRef?.current ||
                    document.body
                }
                panelRender={(panel) => (
                    <div
                        className={styles.customColorPanel}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        {panel}
                        <div className={styles.customColorPanelFooter}>
                            <button
                                type="button"
                                className={styles.customColorOkayBtn}
                                onMouseDown={handleCustomColorOkay}
                            >
                                Okay
                            </button>
                        </div>
                    </div>
                )}
            >
                <button
                    type="button"
                    className={`${styles.colorOption} ${styles.colorOptionCustom} ${
                        isCustomSelected ? styles.colorOptionSelected : ''
                    }`}
                    style={
                        isCustomSelected
                            ? { background: customColorValue }
                            : undefined
                    }
                    aria-label="Pick custom color"
                    title="Custom color"
                    onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                >
                    {isCustomSelected ? (
                        <Icon icon="mdi:check" width={14} color="#fff" />
                    ) : (
                        <Icon
                            icon="mdi:palette-outline"
                            width={14}
                            color="#64748b"
                        />
                    )}
                </button>
            </ColorPicker>
        </div>
    )

    if (readOnly) {
        return (
            <span
                className={`${styles.colorPickerWrap} ${styles.colorPickerWrapReadOnly} ${
                    isDefault ? styles.colorPickerWrapDefault : ''
                }`}
                aria-label="Card color"
            >
                <span
                    className={`${styles.accentBarBtn} ${
                        isDefault ? styles.accentBarBtnDefault : ''
                    }`}
                    style={
                        isDefault
                            ? { borderTopColor: defaultAccent }
                            : { background: value }
                    }
                />
            </span>
        )
    }

    return (
        <Dropdown
            dropdownRender={() => menu}
            trigger={['click']}
            placement="bottomLeft"
            getPopupContainer={() =>
                popupContainerRef?.current || document.body
            }
            overlayClassName={styles.colorPickerOverlay}
        >
            <button
                type="button"
                className={`${styles.colorPickerWrap} ${
                    isDefault ? styles.colorPickerWrapDefault : ''
                }`}
                aria-label="Select card color"
                title="Card color"
            >
                <span
                    className={`${styles.accentBarBtn} ${
                        isDefault ? styles.accentBarBtnDefault : ''
                    }`}
                    style={
                        isDefault
                            ? { borderTopColor: defaultAccent }
                            : { background: value }
                    }
                />
            </button>
        </Dropdown>
    )
}

const StepHierarchyDropdown = ({
    journeys = [],
    selectedStepId,
    open,
    onOpenChange,
    onSelect,
    triggerLabel,
    readOnly = false,
}) => {
    if (readOnly) {
        return <span className={styles.crumbStatic}>{triggerLabel}</span>
    }

    const menu = (
        <div className={styles.crumbDropdown}>
            <div className={styles.crumbDropdownScroll}>
                {journeys.map((journey) => (
                    <div key={journey.id} className={styles.crumbJourneyGroup}>
                        <p className={styles.crumbDropdownTitle}>{journey.title}</p>
                        {(journey.steps ?? []).length > 0 ? (
                            <ul className={styles.crumbDropdownList}>
                                {journey.steps.map((step) => {
                                    const isSelected =
                                        String(step.id) === String(selectedStepId)

                                    return (
                                        <li key={step.id}>
                                            <button
                                                type="button"
                                                className={`${styles.crumbDropdownItem} ${
                                                    isSelected
                                                        ? styles.crumbDropdownItemActive
                                                        : ''
                                                }`}
                                                onClick={() => onSelect(step.id)}
                                            >
                                                <Icon
                                                    icon="mdi:subdirectory-arrow-right"
                                                    width={14}
                                                    className={styles.crumbStepIcon}
                                                    aria-hidden
                                                />
                                                <span className={styles.crumbDropdownLabel}>
                                                    {step.title}
                                                </span>
                                                {isSelected ? (
                                                    <Icon
                                                        icon="mdi:check"
                                                        width={16}
                                                        className={styles.crumbCheck}
                                                        aria-hidden
                                                    />
                                                ) : null}
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>
                        ) : (
                            <p className={styles.crumbDropdownEmpty}>
                                No steps in this journey
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )

    return (
        <Dropdown
            dropdownRender={() => menu}
            trigger={['click']}
            open={open}
            onOpenChange={onOpenChange}
            placement="bottomLeft"
        >
            <button type="button" className={styles.crumbTrigger}>
                <span>{triggerLabel}</span>
                <Icon icon="mdi:chevron-down" width={14} aria-hidden />
            </button>
        </Dropdown>
    )
}

const ReleaseHierarchyDropdown = ({
    releases = [],
    selectedReleaseId,
    open,
    onOpenChange,
    onSelect,
    triggerLabel,
    readOnly = false,
}) => {
    if (readOnly) {
        return <span className={styles.crumbStatic}>{triggerLabel}</span>
    }

    const menu = (
        <div className={styles.crumbDropdown}>
            <ul className={styles.crumbDropdownList}>
                {releases.map((release) => {
                    const isSelected =
                        String(release.id) === String(selectedReleaseId)

                    return (
                        <li key={release.id}>
                            <button
                                type="button"
                                className={`${styles.crumbDropdownItem} ${
                                    isSelected ? styles.crumbDropdownItemActive : ''
                                }`}
                                onClick={() => onSelect(release.id)}
                            >
                                <Icon
                                    icon="mdi:flag-outline"
                                    width={14}
                                    className={styles.crumbReleaseIcon}
                                    aria-hidden
                                />
                                <span className={styles.crumbDropdownLabel}>
                                    {release.title}
                                </span>
                                {isSelected ? (
                                    <Icon
                                        icon="mdi:check"
                                        width={16}
                                        className={styles.crumbCheck}
                                        aria-hidden
                                    />
                                ) : null}
                            </button>
                        </li>
                    )
                })}
            </ul>
        </div>
    )

    return (
        <Dropdown
            dropdownRender={() => menu}
            trigger={['click']}
            open={open}
            onOpenChange={onOpenChange}
            placement="bottomLeft"
        >
            <button type="button" className={styles.crumbTrigger}>
                <Icon
                    icon="mdi:flag-outline"
                    width={14}
                    className={styles.crumbReleaseTriggerIcon}
                    aria-hidden
                />
                <span>{triggerLabel}</span>
                <Icon icon="mdi:chevron-down" width={14} aria-hidden />
            </button>
        </Dropdown>
    )
}

const JourneyHierarchyDropdown = ({
    journeys = [],
    selectedJourneyId,
    open,
    onOpenChange,
    onSelect,
    triggerLabel,
    readOnly = false,
}) => {
    if (readOnly) {
        return <span className={styles.crumbStatic}>{triggerLabel}</span>
    }

    const menu = (
        <div className={styles.crumbDropdown}>
            <ul className={styles.crumbDropdownList}>
                {journeys.map((journey) => {
                    const isSelected =
                        String(journey.id) === String(selectedJourneyId)
                    const stepCount = journey.steps?.length ?? 0

                    return (
                        <li key={journey.id}>
                            <button
                                type="button"
                                className={`${styles.crumbDropdownItem} ${
                                    isSelected ? styles.crumbDropdownItemActive : ''
                                }`}
                                onClick={() => onSelect(journey.id)}
                            >
                                <span className={styles.crumbDropdownLabel}>
                                    {journey.title}
                                </span>
                                <span className={styles.crumbJourneyMeta}>
                                    {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                                </span>
                                {isSelected ? (
                                    <Icon
                                        icon="mdi:check"
                                        width={16}
                                        className={styles.crumbCheck}
                                        aria-hidden
                                    />
                                ) : null}
                            </button>
                        </li>
                    )
                })}
            </ul>
        </div>
    )

    return (
        <Dropdown
            dropdownRender={() => menu}
            trigger={['click']}
            open={open}
            onOpenChange={onOpenChange}
            placement="bottomLeft"
        >
            <button type="button" className={styles.crumbTrigger}>
                <span>{triggerLabel}</span>
                <Icon icon="mdi:chevron-down" width={14} aria-hidden />
            </button>
        </Dropdown>
    )
}

const CardContextBreadcrumb = ({
    cardType,
    context,
    cardId,
    onUpdate,
    onNavigateCard,
    readOnly = false,
}) => {
    const [stepMenuOpen, setStepMenuOpen] = useState(false)
    const [releaseMenuOpen, setReleaseMenuOpen] = useState(false)
    const [journeyMenuOpen, setJourneyMenuOpen] = useState(false)

    const journeys = context?.journeys ?? []
    const releases = context?.releases ?? []

    const handleStepSelect = (stepId) => {
        setStepMenuOpen(false)
        if (String(stepId) === String(context?.stepId)) return

        if (cardType === 'story') {
            if (readOnly) {
                onNavigateCard?.('step', stepId)
                return
            }
            onUpdate?.(cardId, { stepId: String(stepId) })
            return
        }

        onNavigateCard?.('step', stepId)
    }

    const handleReleaseSelect = (releaseId) => {
        setReleaseMenuOpen(false)
        if (String(releaseId) === String(context?.releaseId)) return

        if (cardType === 'story') {
            if (readOnly) {
                onNavigateCard?.('release', releaseId)
                return
            }
            onUpdate?.(cardId, { releaseId: String(releaseId) })
            return
        }

        onNavigateCard?.('release', releaseId)
    }

    const handleJourneySelect = (journeyId) => {
        setJourneyMenuOpen(false)
        if (String(journeyId) === String(context?.journeyId)) return

        if (cardType === 'step') {
            if (readOnly) return
            onUpdate?.(cardId, { journeyId: String(journeyId) })
            return
        }

        onNavigateCard?.('journey', journeyId)
    }

    if (cardType === 'journey') {
        const journeyLabel =
            journeys.find((item) => String(item.id) === String(context?.journeyId))
                ?.title ||
            parseJourneyLabel(context?.journeyTitle || '') ||
            'Untitled journey'

        return (
            <nav className={styles.contextBreadcrumb} aria-label="Card location">
                <JourneyHierarchyDropdown
                    journeys={journeys}
                    selectedJourneyId={context?.journeyId}
                    open={journeyMenuOpen}
                    onOpenChange={setJourneyMenuOpen}
                    onSelect={handleJourneySelect}
                    triggerLabel={journeyLabel}
                    readOnly={readOnly}
                />
                <span className={styles.crumbMeta}>
                    {(context?.stepsCount ?? 0) === 1 ? '1 step' : `${context?.stepsCount ?? 0} steps`}
                </span>
            </nav>
        )
    }

    if (cardType === 'release') {
        const releaseLabel =
            releases.find((item) => String(item.id) === String(context?.releaseId))
                ?.title || 'Untitled release'

        return (
            <nav className={styles.contextBreadcrumb} aria-label="Card location">
                <ReleaseHierarchyDropdown
                    releases={releases}
                    selectedReleaseId={context?.releaseId}
                    open={releaseMenuOpen}
                    onOpenChange={setReleaseMenuOpen}
                    onSelect={handleReleaseSelect}
                    triggerLabel={releaseLabel}
                    readOnly={readOnly}
                />
                <span className={styles.crumbMeta}>
                    {(context?.storiesCount ?? 0) === 1
                        ? '1 story'
                        : `${context?.storiesCount ?? 0} stories`}
                </span>
            </nav>
        )
    }

    if (cardType === 'step' || cardType === 'story') {
        const showRelease = cardType === 'story' && releases.length > 0
        const releaseLabel =
            releases.find((item) => String(item.id) === String(context?.releaseId))
                ?.title ||
            context?.releaseTitle ||
            'Release 1'

        if (cardType === 'step') {
            return (
                <nav className={styles.contextBreadcrumb} aria-label="Card location">
                    <JourneyHierarchyDropdown
                        journeys={journeys}
                        selectedJourneyId={context?.journeyId}
                        open={journeyMenuOpen}
                        onOpenChange={setJourneyMenuOpen}
                        onSelect={handleJourneySelect}
                        triggerLabel={context?.journeyTitle || 'Untitled journey'}
                        readOnly={readOnly}
                    />
                    <Icon
                        icon="mdi:chevron-right"
                        width={14}
                        className={styles.crumbSep}
                        aria-hidden
                    />
                    <span className={styles.crumbStatic}>
                        {context?.stepTitle || 'Untitled step'}
                    </span>
                </nav>
            )
        }

        return (
            <nav className={styles.contextBreadcrumb} aria-label="Card location">
                <span className={styles.crumbStatic}>
                    {context?.journeyTitle || 'Untitled journey'}
                </span>
                <Icon
                    icon="mdi:chevron-right"
                    width={14}
                    className={styles.crumbSep}
                    aria-hidden
                />
                <StepHierarchyDropdown
                    journeys={journeys}
                    selectedStepId={context?.stepId}
                    open={stepMenuOpen}
                    onOpenChange={setStepMenuOpen}
                    onSelect={handleStepSelect}
                    triggerLabel={context?.stepTitle || 'Untitled step'}
                    readOnly={readOnly}
                />
                {showRelease ? (
                    <>
                        <span className={styles.crumbDot} aria-hidden>
                            ·
                        </span>
                        <ReleaseHierarchyDropdown
                            releases={releases}
                            selectedReleaseId={context?.releaseId}
                            open={releaseMenuOpen}
                            onOpenChange={setReleaseMenuOpen}
                            onSelect={handleReleaseSelect}
                            triggerLabel={releaseLabel}
                            readOnly={readOnly}
                        />
                    </>
                ) : null}
            </nav>
        )
    }

    return null
}

const stripHtml = (html = '') =>
    String(html)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const ACTIVITY_MODULE_BY_CARD_TYPE = {
    story: 'story',
    step: 'step',
    journey: 'journey',
    release: 'release',
}

const StoryMapCardDetailModal = forwardRef(({
    open,
    cardType,
    card,
    context,
    projectId,
    linkCatalog = null,
    onClose,
    onUpdate,
    onAddComment,
    onDeleteComment,
    onEditComment,
    onLoadComments,
    onLoadActivity,
    onLoadCardDetail,
    onCopyCardLink,
    onNavigateCard,
    readOnly = false,
    canDelete = false,
}, ref) => {
    const titleInputRef = useRef(null)
    const skipTitleBlurCommitRef = useRef(false)
    const drawerContainerRef = useRef(null)
    const attachmentInputRef = useRef(null)
    const { userProfile } = useSelector(loginState)
    const activityAuthorName = useMemo(
        () =>
            userProfile?.name ||
            userProfile?.fullName ||
            userProfile?.username ||
            'You',
        [userProfile]
    )
    const [titleEditing, setTitleEditing] = useState(false)
    const [titleDraft, setTitleDraft] = useState('')
    const [description, setDescription] = useState('')
    const [accentColor, setAccentColor] = useState('#ffffff')
    const [storyStatus, setStoryStatus] = useState(DEFAULT_STORY_STATUS)
    const [attachments, setAttachments] = useState([])
    const [comments, setComments] = useState([])
    const [loadingComments, setLoadingComments] = useState(false)
    const [loadingMoreComments, setLoadingMoreComments] = useState(false)
    const [commentsHasMore, setCommentsHasMore] = useState(false)
    const commentsSkipRef = useRef(0)
    const loadingMoreCommentsRef = useRef(false)
    const [deleteAttachmentId, setDeleteAttachmentId] = useState(null)
    const [previewImageAttachment, setPreviewImageAttachment] = useState(null)
    const [uploadingAttachment, setUploadingAttachment] = useState(false)
    const [loadingCardDetail, setLoadingCardDetail] = useState(false)
    const [footerTab, setFooterTab] = useState('comments')

    const displayTitle = useMemo(() => {
        if (cardType === 'step') {
            return parseStepLabel(card?.title || '').name
        }
        if (cardType === 'journey') {
            return parseJourneyLabel(card?.title || '')
        }
        if (cardType === 'release') {
            return card?.title || 'Untitled release'
        }
        return card?.title || 'Untitled story'
    }, [card?.title, cardType])

    const releaseDateValue = useMemo(() => {
        if (cardType !== 'release' || !card) {
            return { start: null, end: null }
        }

        if (card.start != null || card.end != null) {
            return {
                start: card.start ?? null,
                end: card.end ?? null,
            }
        }

        const { start, due } = parseReleaseDates(card.dateRange || '')
        const toTimestamp = (label) => {
            if (!label) return null
            const parsed = dayjs(label, 'D MMM YYYY')
            return parsed.isValid() ? parsed.startOf('day').valueOf() : null
        }

        return {
            start: toTimestamp(start),
            end: toTimestamp(due),
        }
    }, [card, cardType])

    const supportsColorPicker = cardType === 'story' || cardType === 'step'
    const showsHeaderAccent = supportsColorPicker

    const cardColor = useMemo(
        () => normalizeHexColor(card?.accentColor || card?.color) || '#ffffff',
        [card?.accentColor, card?.color]
    )

    useEffect(() => {
        if (!open || !card) return

        setTitleDraft(displayTitle)
        setTitleEditing(false)
        setDescription(card.description || '')
        setAccentColor(cardColor)
        setStoryStatus(normalizeStoryStatus(card.status))
        setAttachments(card.attachments || [])
        setDeleteAttachmentId(null)
        setPreviewImageAttachment(null)
        setFooterTab('comments')
    }, [open, card?.id, cardColor, displayTitle, card])

    useEffect(() => {
        if (!open || !card?.id || !onLoadCardDetail || !cardType) {
            setLoadingCardDetail(false)
            return undefined
        }

        if (!isMongoObjectId(card.id)) {
            setLoadingCardDetail(false)
            return undefined
        }

        let cancelled = false
        setLoadingCardDetail(true)

        ;(async () => {
            try {
                await onLoadCardDetail(card.id, cardType)
            } finally {
                if (!cancelled) {
                    setLoadingCardDetail(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [open, card?.id, cardType, onLoadCardDetail])

    useEffect(() => {
        if (!open || !card?.id) return undefined

        let cancelled = false

        const loadComments = async () => {
            setLoadingComments(true)
            setComments([])
            setCommentsHasMore(false)
            commentsSkipRef.current = 0

            if (onLoadComments) {
                const result = await onLoadComments(card.id, {
                    skip: 0,
                    limit: COMMENT_PAGE_SIZE,
                })
                if (!cancelled && result?.success) {
                    // API returns newest-first; show oldest → newest (latest at bottom)
                    const nextComments = [...(result.comments || [])].reverse()
                    setComments(nextComments)
                    setCommentsHasMore(Boolean(result.isNext))
                    commentsSkipRef.current = nextComments.length
                }
            } else {
                setComments(card.comments || [])
            }

            if (!cancelled) setLoadingComments(false)
        }

        loadComments()

        return () => {
            cancelled = true
        }
        // Only reload when the drawer opens or the card changes — not when a
        // comment is appended locally (that used to flash the skeleton).
    }, [open, card?.id, onLoadComments]) // eslint-disable-line react-hooks/exhaustive-deps

    const loadMoreComments = useCallback(async () => {
        if (
            !card?.id ||
            !onLoadComments ||
            loadingComments ||
            loadingMoreCommentsRef.current ||
            !commentsHasMore
        ) {
            return
        }

        loadingMoreCommentsRef.current = true
        setLoadingMoreComments(true)

        try {
            const result = await onLoadComments(card.id, {
                skip: commentsSkipRef.current,
                limit: COMMENT_PAGE_SIZE,
            })

            if (result?.success) {
                const incoming = result.comments || []
                setComments((prev) => {
                    const existingIds = new Set(prev.map((item) => item.id))
                    // Older page is also newest-first from API → reverse, then prepend
                    const older = [...incoming]
                        .reverse()
                        .filter((item) => !existingIds.has(item.id))
                    return older.length > 0 ? [...older, ...prev] : prev
                })
                setCommentsHasMore(Boolean(result.isNext))
                commentsSkipRef.current += incoming.length
            }
        } finally {
            loadingMoreCommentsRef.current = false
            setLoadingMoreComments(false)
        }
    }, [card?.id, commentsHasMore, loadingComments, onLoadComments])

    const persist = useCallback(
        (updates) => {
            if (readOnly) return
            if (!card?.id) return

            const changed = {}

            if (updates.title != null && updates.title !== (card.title ?? '')) {
                changed.title = updates.title
            }

            if (updates.description != null) {
                const nextDesc = normalizeHtml(updates.description)
                const currentDesc = normalizeHtml(
                    card.description ?? description ?? ''
                )
                if (!descriptionsAreEqual(nextDesc, currentDesc)) {
                    changed.description = nextDesc
                }
            }

            if (updates.color != null || updates.accentColor != null) {
                const nextColor = normalizeHexColor(
                    updates.color ?? updates.accentColor
                )
                const currentColor = normalizeHexColor(
                    card.color ?? card.accentColor ?? accentColor
                )
                if (nextColor !== currentColor) {
                    changed.color = nextColor
                    changed.accentColor = nextColor
                }
            }

            if (updates.status != null) {
                const nextStatus = normalizeStoryStatus(updates.status)
                const currentStatus = normalizeStoryStatus(
                    card.status ?? storyStatus
                )
                if (nextStatus !== currentStatus) {
                    changed.status = nextStatus
                }
            }

            if (updates.start !== undefined || updates.end !== undefined) {
                const nextStart =
                    updates.start !== undefined ? updates.start : card.start
                const nextEnd =
                    updates.end !== undefined ? updates.end : card.end
                if (nextStart !== card.start || nextEnd !== card.end) {
                    changed.start = nextStart
                    changed.end = nextEnd
                    if (updates.dateRange != null) {
                        changed.dateRange = updates.dateRange
                    }
                }
            }

            if (updates.attachments != null) {
                const serializeAttachments = (list) =>
                    (list ?? [])
                        .map((item) => String(item.id ?? item._id ?? ''))
                        .join('\0')
                if (
                    serializeAttachments(updates.attachments) !==
                    serializeAttachments(card.attachments ?? attachments)
                ) {
                    changed.attachments = updates.attachments
                }
            }

            if (
                updates.comments != null &&
                updates.comments.length !== comments.length
            ) {
                changed.comments = updates.comments
            }

            if (Object.keys(changed).length === 0) return

            const nextDescription =
                changed.description != null ? changed.description : description
            const hasDesc = Boolean(stripHtml(nextDescription))
            const nextCommentsCount =
                changed.comments?.length ?? comments.length

            onUpdate(card.id, {
                ...changed,
                hasDescription: hasDesc,
                showDescriptionIcon: hasDesc,
                attachmentsCount:
                    changed.attachments?.length ?? attachments.length,
                showAttachmentIcon:
                    (changed.attachments?.length ?? attachments.length) > 0,
                commentsCount: nextCommentsCount,
                ...(nextCommentsCount > 0
                    ? { hasComments: true, showCommentsIcon: true }
                    : {}),
            })
        },
        [
            accentColor,
            attachments,
            card,
            comments.length,
            description,
            onUpdate,
            readOnly,
            storyStatus,
        ]
    )

    const commitTitle = useCallback(() => {
        const untitledLabel =
            cardType === 'step'
                ? 'Untitled step'
                : cardType === 'journey'
                  ? 'Untitled journey'
                  : cardType === 'release'
                    ? 'Untitled release'
                    : 'Untitled story'
        const nextTitle = resolveEditTitleName(
            titleDraft,
            { current: titleDraft },
            titleInputRef,
            untitledLabel
        )
        setTitleDraft(nextTitle)
        setTitleEditing(false)

        let fullTitle = nextTitle
        if (cardType === 'step') {
            const { version } = parseStepLabel(card?.title || '')
            fullTitle = `${version} ${nextTitle}`
        } else if (cardType === 'journey') {
            const journeyNumber = (context?.journeyIndex ?? 0) + 1
            fullTitle = `${journeyNumber}. ${nextTitle}`
        }

        if (fullTitle === (card?.title || '')) return

        persist({ title: fullTitle })
    }, [card?.title, cardType, context?.journeyIndex, persist, titleDraft])

    const commitTitleRef = useRef(commitTitle)
    commitTitleRef.current = commitTitle
    const titleEditingRef = useRef(titleEditing)
    titleEditingRef.current = titleEditing

    const flushPendingEdits = useCallback(() => {
        if (readOnly) return
        if (titleEditingRef.current) {
            commitTitleRef.current()
        }
    }, [readOnly])

    useImperativeHandle(
        ref,
        () => ({
            flushPendingEdits,
        }),
        [flushPendingEdits]
    )

    const handleClose = useCallback(() => {
        flushPendingEdits()
        onClose?.()
    }, [flushPendingEdits, onClose])

    const startTitleEditing = useCallback(() => {
        if (readOnly) return
        setTitleDraft(displayTitle)
        setTitleEditing(true)
    }, [displayTitle, readOnly])

    useEffect(() => {
        if (!titleEditing) return undefined

        const frame = window.requestAnimationFrame(() => {
            titleInputRef.current?.focus()
            titleInputRef.current?.select()
        })

        return () => window.cancelAnimationFrame(frame)
    }, [titleEditing])

    const handleTitleBlur = useCallback(() => {
        if (skipTitleBlurCommitRef.current) return
        commitTitle()
    }, [commitTitle])

    const handleTitleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            skipTitleBlurCommitRef.current = true
            commitTitle()
            window.requestAnimationFrame(() => {
                skipTitleBlurCommitRef.current = false
            })
        }
        if (event.key === 'Escape') {
            skipTitleBlurCommitRef.current = true
            setTitleDraft(displayTitle)
            setTitleEditing(false)
            titleInputRef.current?.blur()
            window.requestAnimationFrame(() => {
                skipTitleBlurCommitRef.current = false
            })
        }
    }

    const handleColorChange = (color) => {
        const normalizedColor = normalizeHexColor(color)
        const currentColor = normalizeHexColor(
            card.color ?? card.accentColor ?? accentColor
        )
        if (normalizedColor === currentColor) return
        setAccentColor(normalizedColor)
        persist({
            color: normalizedColor,
            accentColor: normalizedColor,
        })
    }

    const handleStatusChange = (nextStatus) => {
        const normalizedStatus = normalizeStoryStatus(nextStatus)
        if (normalizedStatus === storyStatus) return
        setStoryStatus(normalizedStatus)
        persist({ status: normalizedStatus })
    }

    const handleReleaseDateChange = useCallback(
        (nextValue) => {
            const start = nextValue?.start ?? null
            const end = nextValue?.end ?? null
            if (start === card.start && end === card.end) return
            persist({
                start,
                end,
                dateRange: formatReleaseDateRange(start, end),
            })
        },
        [card.end, card.start, persist]
    )

    const handleAttachmentFiles = async (fileList) => {
        const files = Array.from(fileList || [])
        if (!files.length || !card?.id || uploadingAttachment) return

        const module = getStoryMapAttachmentModule(cardType)
        const validFiles = files.filter(isAllowedStoryMapAttachment)
        const invalidCount = files.length - validFiles.length

        if (invalidCount > 0) {
            showActionNotification({
                type: 'error',
                message: 'Only image, PDF, and CSV files are allowed.',
            })
        }

        if (!validFiles.length) return

        if (!isMongoObjectId(card.id)) {
            showActionNotification({
                type: 'error',
                message: 'Save this card before uploading attachments.',
            })
            return
        }

        setUploadingAttachment(true)

        try {
            const uploadRequest = await getStoryMapAttachmentUploadUrls(card.id, {
                module,
                files: validFiles.map((file) => ({
                    file: file.name,
                    type: file.type || 'application/octet-stream',
                })),
            })

            if (!uploadRequest.success || !uploadRequest.result?.length) {
                showActionNotification({
                    type: 'error',
                    message: uploadRequest.message || 'Failed to upload attachment',
                })
                return
            }

            const uploadedAttachments = []

            for (let index = 0; index < uploadRequest.result.length; index += 1) {
                const uploadMeta = uploadRequest.result[index]
                const file = validFiles[index]
                if (!uploadMeta?.url || !file) continue

                const uploadResult = await uploadAttachmentToAWS({
                    url: uploadMeta.url,
                    data: file,
                })

                if (!uploadResult?.data?.ok) {
                    showActionNotification({
                        type: 'error',
                        message: `Failed to upload ${file.name}`,
                    })
                    continue
                }

                uploadedAttachments.push({
                    id: String(uploadMeta.fileName || `att-${Date.now()}-${index}`),
                    name: file.name,
                    type: 'file',
                    url: uploadMeta.s3Url || uploadMeta.url,
                    fileName: uploadMeta.fileName,
                })
            }

            if (!uploadedAttachments.length) return

            const nextAttachments = [...attachments, ...uploadedAttachments]
            setAttachments(nextAttachments)
            persist({ attachments: nextAttachments })
            showActionNotification({
                type: 'success',
                message:
                    uploadedAttachments.length === 1
                        ? 'Attachment uploaded successfully'
                        : `${uploadedAttachments.length} attachments uploaded successfully`,
            })
        } finally {
            setUploadingAttachment(false)
            if (attachmentInputRef.current) {
                attachmentInputRef.current.value = ''
            }
        }
    }

    const confirmDeleteAttachment = async () => {
        const attachment = attachments.find((item) => item.id === deleteAttachmentId)
        if (!attachment) {
            setDeleteAttachmentId(null)
            return
        }

        if (isMongoObjectId(card?.id) && attachment.fileName) {
            const result = await deleteStoryMapAttachment(card.id, {
                module: getStoryMapAttachmentModule(cardType),
                fileName: attachment.fileName,
            })

            if (!result.success) {
                showActionNotification({
                    type: 'error',
                    message: result.message || 'Failed to delete attachment',
                })
                return
            }
        }

        const nextAttachments = attachments.filter(
            (item) => item.id !== deleteAttachmentId
        )
        setAttachments(nextAttachments)
        persist({ attachments: nextAttachments })
        setDeleteAttachmentId(null)
    }

    const handleDescriptionSave = useCallback(
        (html) => {
            if (readOnly) return

            const normalizedHtml = normalizeHtml(html)
            const baseline = card?.description ?? description ?? ''
            if (descriptionsAreEqual(normalizedHtml, baseline)) return

            const previousDescription = baseline || ''
            setDescription(normalizedHtml)
            persist({ description: normalizedHtml })

            if (card?.id && isMongoObjectId(card.id)) {
                prependStoryMapDescriptionActivity({
                    cardId: card.id,
                    oldValue: previousDescription,
                    newValue: normalizedHtml,
                    authorName: activityAuthorName,
                    authorId: userProfile?._id,
                    moduleType: ACTIVITY_MODULE_BY_CARD_TYPE[cardType] || 'story',
                })
            }
        },
        [
            activityAuthorName,
            card?.description,
            card?.id,
            cardType,
            description,
            persist,
            readOnly,
            userProfile?._id,
        ]
    )

    const handlePostComment = useCallback(
        async (text) => {
            if (!text?.trim() || !onAddComment || !card?.id) {
                return { success: false }
            }

            const result = await onAddComment(card.id, cardType, text)

            if (!result?.success || !result.comment) {
                return result || { success: false }
            }

            setComments((prev) => [...prev, result.comment])
            // Update board meta only; don't refetch the list (avoids skeleton flash)
            persist({
                comments: [...comments, result.comment],
            })

            return result
        },
        [card?.id, cardType, comments, onAddComment, persist]
    )

    const handleDeleteComment = useCallback(
        async (commentId) => {
            if (!commentId || !onDeleteComment || !card?.id) {
                return { success: false }
            }

            const result = await onDeleteComment(card.id, cardType, commentId)
            if (!result?.success) {
                return result || { success: false }
            }

            setComments((prev) =>
                prev.filter((comment) => String(comment.id) !== String(commentId))
            )

            return result
        },
        [card?.id, cardType, onDeleteComment]
    )

    const handleEditComment = useCallback(
        async (commentId, content) => {
            if (!commentId || !onEditComment || !card?.id) {
                return { success: false }
            }

            const result = await onEditComment(
                card.id,
                cardType,
                commentId,
                content
            )
            if (!result?.success) {
                return result || { success: false }
            }

            const nextText = result.text || content
            setComments((prev) =>
                prev.map((comment) =>
                    String(comment.id) === String(commentId)
                        ? { ...comment, text: nextText }
                        : comment
                )
            )

            return result
        },
        [card?.id, cardType, onEditComment]
    )

    const currentUserNames = useMemo(
        () =>
            [
                userProfile?.name,
                userProfile?.userName,
                userProfile?.username,
                userProfile?.fullName,
            ].filter(Boolean),
        [userProfile]
    )

    if (!open || !card) return null

    const imageAttachments = attachments.filter((item) => isAttachmentImage(item.name))
    const fileAttachments = attachments.filter((item) => !isAttachmentImage(item.name))
    const pendingDeleteAttachment = attachments.find(
        (item) => item.id === deleteAttachmentId
    )
    const drawerTopBarTitle = DRAWER_TITLE_BY_TYPE[cardType] || 'Card details'

    return (
        <div
            ref={drawerContainerRef}
            className={`${styles.drawerContainer} ${
                readOnly ? styles.drawerReadOnly : ''
            }`}
        >
            <div className={styles.topBar}>
                <div className={styles.topBarLeft}>
                    {showsHeaderAccent ? (
                        loadingCardDetail ? (
                            <Skeleton.Input active className={styles.skColorBar} />
                        ) : (
                            <ColorDropdown
                                value={accentColor}
                                onChange={handleColorChange}
                                cardType={cardType}
                                popupContainerRef={drawerContainerRef}
                                readOnly={readOnly}
                            />
                        )
                    ) : null}
                    <span className={styles.topBarTitle}>{drawerTopBarTitle}</span>
                </div>
                <div className={styles.topBarRight}>
                    {cardType === 'step' || cardType === 'story' ? (
                        <Tooltip title="Copy link" placement="bottom">
                            <button
                                type="button"
                                className={styles.topBarCopyBtn}
                                onClick={() => onCopyCardLink?.(cardType, card?.id)}
                                aria-label="Copy link"
                            >
                                <Icon icon="mdi:link-variant" width={18} height={18} />
                            </button>
                        </Tooltip>
                    ) : null}
                    <button
                        type="button"
                        className={styles.topBarCloseBtn}
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        <Icon icon="ph:arrow-line-right" width={18} height={18} />
                    </button>
                </div>
            </div>

            <div className={styles.drawerBody}>
                {loadingCardDetail ? (
                    <StoryMapCardDetailSkeleton cardType={cardType} />
                ) : (
                    <>
                <div className={styles.titleSection}>
                    <div className={styles.titleRow}>
                        {titleEditing ? (
                            <input
                                ref={titleInputRef}
                                type="text"
                                className={`${styles.titleInput} ${styles.editing} ${
                                    !isUntitledName(titleDraft) ? styles.hasValue : ''
                                }`}
                                value={titleDraft}
                                onChange={(event) => setTitleDraft(event.target.value)}
                                onBlur={handleTitleBlur}
                                onKeyDown={handleTitleKeyDown}
                                aria-label="Card title"
                            />
                        ) : readOnly ? (
                            <span
                                className={`${styles.titleDisplay} ${styles.titleDisplayReadOnly} ${
                                    !isUntitledName(titleDraft) ? styles.hasValue : ''
                                }`}
                            >
                                {titleDraft || displayTitle}
                            </span>
                        ) : (
                            <button
                                type="button"
                                className={`${styles.titleDisplay} ${
                                    !isUntitledName(titleDraft) ? styles.hasValue : ''
                                }`}
                                onClick={startTitleEditing}
                            >
                                {titleDraft || displayTitle}
                            </button>
                        )}
                        {cardType === 'story' ? (
                            <div className={styles.storyStatusRow}>
                                <StoryMapStatusDropdown
                                    value={storyStatus}
                                    onChange={handleStatusChange}
                                    readOnly={readOnly}
                                    getPopupContainer={() =>
                                        drawerContainerRef.current ||
                                        document.body
                                    }
                                />
                            </div>
                        ) : null}
                    </div>
                    <CardContextBreadcrumb
                        cardType={cardType}
                        context={context}
                        cardId={card?.id}
                        onUpdate={onUpdate}
                        onNavigateCard={onNavigateCard}
                        readOnly={readOnly}
                    />
                </div>

                <div className={styles.contentScroll}>
                <div className={styles.scrollBody}>
                    {cardType !== 'release' ? (
                    <section className={styles.section}>
                        <div className={styles.sectionLabelRow}>
                            <span className={styles.sectionLabel}>Attachments</span>
                            {attachments.length > 0 ? (
                                <span className={styles.attachmentCount}>
                                    {attachments.length}
                                </span>
                            ) : null}
                        </div>
                        <div className={styles.attachmentsPanel}>
                            {!readOnly ? (
                                <>
                            <input
                                ref={attachmentInputRef}
                                type="file"
                                accept={STORY_MAP_ATTACHMENT_ACCEPT}
                                multiple
                                className={styles.attachmentFileInput}
                                onChange={(event) =>
                                    handleAttachmentFiles(event.target.files)
                                }
                            />
                            <button
                                type="button"
                                className={styles.attachmentUploadZone}
                                onClick={() => attachmentInputRef.current?.click()}
                                disabled={uploadingAttachment}
                            >
                                <span className={styles.attachmentUploadIcon}>
                                    <Icon
                                        icon={
                                            uploadingAttachment
                                                ? 'line-md:uploading-loop'
                                                : 'mdi:cloud-upload-outline'
                                        }
                                        width={22}
                                    />
                                </span>
                                <span className={styles.attachmentUploadCopy}>
                                    <strong>
                                        {uploadingAttachment
                                            ? 'Uploading...'
                                            : 'Upload image, PDF or CSV'}
                                    </strong>
                                    <span>PNG, JPG, GIF, SVG, WEBP, PDF, CSV</span>
                                </span>
                            </button>
                                </>
                            ) : null}
                            {attachments.length > 0 ? (
                                <div className={styles.attachmentGroups}>
                                    {imageAttachments.length > 0 ? (
                                        <ul className={styles.attachmentImageGrid}>
                                            {imageAttachments.map((item) => (
                                                <AttachmentImageCard
                                                    key={item.id}
                                                    item={item}
                                                    onPreview={setPreviewImageAttachment}
                                                    onDelete={
                                                        readOnly || !canDelete
                                                            ? undefined
                                                            : setDeleteAttachmentId
                                                    }
                                                />
                                            ))}
                                        </ul>
                                    ) : null}
                                    {fileAttachments.length > 0 ? (
                                        <ul className={styles.attachmentFileList}>
                                            {fileAttachments.map((item) => (
                                                <AttachmentFileRow
                                                    key={item.id}
                                                    item={item}
                                                    onDelete={
                                                        readOnly || !canDelete
                                                            ? undefined
                                                            : setDeleteAttachmentId
                                                    }
                                                />
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                            ) : (
                                <p className={styles.attachmentEmptyHint}>
                                    {readOnly
                                        ? 'No attachments.'
                                        : 'No files uploaded yet.'}
                                </p>
                            )}
                        </div>
                    </section>
                    ) : null}

                    {cardType === 'release' ? (
                        <section className={styles.releaseDatesSection}>
                            <span className={styles.releaseDatesLabel}>
                                Due date
                            </span>
                            <div className={styles.releaseDatesContent}>
                                {readOnly ? (
                                    <span>
                                        {releaseDateValue?.start || releaseDateValue?.end
                                            ? [
                                                  releaseDateValue?.start
                                                      ? dayjs(releaseDateValue.start).format(
                                                            'DD/MM/YY'
                                                        )
                                                      : null,
                                                  releaseDateValue?.end
                                                      ? dayjs(releaseDateValue.end).format(
                                                            'DD/MM/YY'
                                                        )
                                                      : null,
                                              ]
                                                  .filter(Boolean)
                                                  .join(' – ') || 'No due date'
                                            : 'No due date'}
                                    </span>
                                ) : (
                                <DateRangePicker
                                    value={releaseDateValue}
                                    onChange={handleReleaseDateChange}
                                    placeholder="No due date"
                                    parentComponent="storyMapReleaseDetail"
                                    getPopupContainer={() =>
                                        drawerContainerRef.current ||
                                        document.body
                                    }
                                />
                                )}
                            </div>
                        </section>
                    ) : null}

                    <section className={styles.descriptionSection}>
                        <span className={styles.sectionLabel}>Description</span>
                        <div className={styles.descriptionEditorHost}>
                            <StoryMapDescriptionEditor
                                key={`${cardType}-${card.id}`}
                                value={description || ''}
                                onSave={handleDescriptionSave}
                                placeholder="What is this task about?"
                                projectId={projectId}
                                readOnly={readOnly}
                                linkCatalog={linkCatalog}
                            />
                        </div>
                    </section>
                </div>
                </div>

                <StoryMapCommentsFooter
                    projectId={projectId}
                    cardId={card?.id}
                    open={open}
                    footerTab={footerTab}
                    onFooterTabChange={setFooterTab}
                    comments={comments}
                    loadingComments={loadingComments}
                    loadingMoreComments={loadingMoreComments}
                    commentsHasMore={commentsHasMore}
                    onLoadMoreComments={loadMoreComments}
                    onPostComment={handlePostComment}
                    onDeleteComment={handleDeleteComment}
                    onEditComment={handleEditComment}
                    onLoadActivity={onLoadActivity}
                    readOnly={readOnly}
                    canDelete={canDelete}
                    currentUserNames={currentUserNames}
                />
                    </>
                )}
            </div>

            <AttachmentImagePreviewModal
                item={previewImageAttachment}
                open={Boolean(previewImageAttachment)}
                onClose={() => setPreviewImageAttachment(null)}
            />

            <StoryMapDeleteConfirm
                open={Boolean(deleteAttachmentId && pendingDeleteAttachment)}
                title="Delete this attachment?"
                message="This attachment will be removed from the project. This cannot be undone."
                detail={pendingDeleteAttachment?.name}
                confirmingText="Deleting..."
                onCancel={() => setDeleteAttachmentId(null)}
                onConfirm={confirmDeleteAttachment}
            />
        </div>
    )
})

StoryMapCardDetailModal.displayName = 'StoryMapCardDetailModal'

export default StoryMapCardDetailModal
